import { useRef } from "react";
import {
  OnlineStockData,
  Product,
  ProductListConfig,
  Variant,
} from "../../types/types";
import {
  fetchOnlineStockData,
  shouldFetchOnlineStock,
} from "@~/utils/fetchOnlineStockData";

export const isValidSize = (size: string, product: Product) =>
  product.variants.some((v: Variant) => v.differentiators?.size === size);

export const isValidColour = (colour: string, product: Product): boolean =>
  product.variants.some((v: Variant) => v.differentiators?.colour === colour);

export const getVariant = ({
  product,
  userSelectedColour,
  userSelectedSize,
}: {
  product: Product;
  userSelectedColour: string | null;
  userSelectedSize: string | null;
}): Variant | null => {
  const variants: Variant[] = product.variants;

  // 1 user has selected both valid size and colour - return relevant variant
  if (userSelectedColour && userSelectedSize) {
    const variant = variants.find(
      (v) =>
        v.differentiators?.size === userSelectedSize &&
        v.differentiators?.colour === userSelectedColour,
    );

    if (variant) {
      return variant;
    }
  }

  // 2 user has selected a colour - return first variant that matches
  if (userSelectedColour) {
    const variant = variants.find(
      (v) => v.differentiators?.colour === userSelectedColour,
    );

    if (variant) {
      return variant;
    }
  }

  // 3 user has selected a size - return first variant that matches
  if (userSelectedSize) {
    const variant = variants.find(
      (v) => v.differentiators?.size === userSelectedSize,
    );

    if (variant) {
      return variant;
    }
  }

  // 4 an initialVariantId was supplied - return that variant
  if (product.initialVariantId) {
    const variant = variants.find((v) => v.id === product.initialVariantId);

    if (variant) {
      return variant;
    }
  }

  // 5 an initialSkuId was supplied - return that variant
  if (product.initialSkuId) {
    const variant = variants.find(
      (v) => v.aliases?.skuId === product.initialSkuId,
    );

    if (variant) {
      return variant;
    }
  }

  // 6 return default variant or first one in the list
  return (
    variants?.find((v) => v.id === product.defaultVariant.id) ||
    variants[0] ||
    null
  );
};

/**
 * Returns a stable promise for the online stock data of the given SKU.
 * Results are cached by SKU so variant switching reuses resolved promises.
 * The cache is cleared when the product reference changes.
 */
export function useOnlineStockPromise(
  product: Product,
  skuId: string | undefined,
  config: ProductListConfig | undefined,
): Promise<OnlineStockData | undefined> {
  const cacheRef = useRef<Map<string, Promise<OnlineStockData | undefined>>>(
    new Map(),
  );
  const prevProductRef = useRef<Product | null>(null);

  // Online stock fetches are client-only. On the server, return a resolved
  // empty promise so React.use() never suspends during SSR.
  if (typeof window === "undefined") {
    return Promise.resolve(undefined);
  }

  if (prevProductRef.current !== product) {
    cacheRef.current = new Map();
    prevProductRef.current = product;
  }

  if (
    skuId &&
    config &&
    shouldFetchOnlineStock(config) &&
    !cacheRef.current.has(skuId)
  ) {
    cacheRef.current.set(skuId, getOnlineStockData(product, skuId));
  }

  return skuId && config && shouldFetchOnlineStock(config)
    ? (cacheRef.current.get(skuId) ?? Promise.resolve(undefined))
    : Promise.resolve(undefined);
}

const getOnlineStockData = async (
  product: Product,
  skuId: string | undefined,
): Promise<OnlineStockData | undefined> => {
  if (!skuId) {
    return Promise.resolve(undefined);
  }

  const initialStock = product.initialVariantOnlineStock
    ? await product.initialVariantOnlineStock
    : undefined;

  if (skuId === initialStock?.skuId) {
    return initialStock;
  }

  return fetchOnlineStockData([skuId]).then((data) => data[0]);
};
